import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

import { type DistrictTemplateFilters } from './getDistrictTemplateById';

const PATH_TEMPLATE = '/district-templates/districtKey/{districtKey}';
type THIS_API = ApiDefinition[typeof PATH_TEMPLATE]['GET'];
type Input = THIS_API['params'] & Partial<THIS_API['query']>;
type Output = THIS_API['response'];

/**
 * SWR-based hook for fetching a single district template by districtKey, with optional chapter and joint filters.
 * Returns cached data across remounts to prevent unnecessary reloads.
 */
export const useDistrictTemplateByKey = (
  params: (THIS_API['params'] & DistrictTemplateFilters) | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output> => {
  const key = useMemo((): SwrKey => {
    if (!params?.districtKey) return null;
    const { districtKey, chapter, joint } = params;
    const path = buildPath<THIS_API>(PATH_TEMPLATE, { districtKey });
    return [path, { params: { chapter, joint } }];
  }, [params]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * Fetches a single district template by its districtKey.
 * @deprecated Please use `useDistrictTemplateByKey` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getDistrictTemplateByKey = async ({ districtKey, chapter, joint }: Input): Promise<Output> => {
  const path = buildPath<THIS_API>(PATH_TEMPLATE, { districtKey });
  const response = await noodleApiClient.get(path, { params: { chapter, joint } });
  return response.data;
};

export default getDistrictTemplateByKey;
